<script setup>
import { ref, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { DxList } from 'devextreme-vue/list'

import { getI18n } from '@/plugins/i18n'

import { useAccountingStore } from '@/stores/accounting.store'
import { useWebSiteStore } from '@/stores/web-site.store'

import { useODataSource } from '@/composables/data-grid'
import { useHighlightNewRow } from '@/composables/scroll'

import BottomTabsView from '@/components/shared/BottomTabsView.vue'
import FilterSortSelector from '@/components/shared/FilterSortSelector.vue'
import InvoiceEdit from '@/components/accounting/InvoiceEdit.vue'
import InvoiceDetail from '@/components/accounting/InvoiceDetail.vue'
import InvoiceListItem from '@/components/accounting/InvoiceListItem.vue'

const i18n = getI18n()
const route = useRoute()
const router = useRouter()
const accountingStore = useAccountingStore()
const webSiteStore = useWebSiteStore()
const { highlightedRowId, highlightRowAsync } = useHighlightNewRow()

const listContainer = ref(null)
let listInstance = null

const isEmptyDataSource = ref(false)
const detailItem = ref(null)
const editItem = ref(null)

const ALL_STATUSES_TAB = 'all'
const statusTabs = [
  { name: ALL_STATUSES_TAB, label: i18n.t('common.allFeminine') },
  ...accountingStore.getLocalizedInvoiceStatuses(2)
    .filter(status => status.id !== accountingStore.invoiceStatusEnum.CANCELLED)
    .map(status => ({ name: status.id, label: status.name })),
]

webSiteStore.fabActions = [
  {
    id: 'add',
    icon: 'mdi-file-document-plus-outline',
    onClick: () => { editItem.value = {} },
  },
]

const dataSource = useODataSource(accountingStore.api.getAbsoluteUrl() + accountingStore.api.invoicesUrl, {
  onChanged: () => {
    isEmptyDataSource.value = dataSource.totalCount() === 0
  },
  searchExpr: 'search_field',
  customParamsFn: () => (webSiteStore.invoicesStatusFilter === null ? null : { status: webSiteStore.invoicesStatusFilter }),
})

const openRouteInvoiceAsync = async (routeInvoiceNumbers) => {
  const [invoiceNumber, ...otherNumbers] = [routeInvoiceNumbers ?? []].flat()
  if (!invoiceNumber || otherNumbers.length) return
  const query = { ...route.query }
  delete query.invoice_number
  router.replace({ query })
  webSiteStore.invoicesStatusFilter = null
  const [invoice] = await dataSource.store().load({ filter: ['invoice_number', '=', invoiceNumber] })
  if (invoice) detailItem.value = invoice
}
watch(() => route.query.invoice_number, openRouteInvoiceAsync, { immediate: true })

const activeStatusTab = computed(() => webSiteStore.invoicesStatusFilter ?? ALL_STATUSES_TAB)

const onStatusFilterChange = (tab) => {
  webSiteStore.invoicesStatusFilter = tab === ALL_STATUSES_TAB ? null : tab
  dataSource.reload()
}

const onDetailChanged = () => {
  detailItem.value = null
  dataSource.reload()
}

const onInvoiceCreated = async (invoiceId) => {
  editItem.value = null
  webSiteStore.activateUpdatedDateSort(route.name)
  dataSource.sort(webSiteStore.buildSortOrderBy(route.name))
  dataSource.pageIndex(0)
  await dataSource.reload()
  await highlightRowAsync(invoiceId, listContainer)
  listInstance?.repaint()
}
</script>

<template>
  <bottom-tabs-view
    :model-value="activeStatusTab"
    :tabs="statusTabs"
    @update:model-value="onStatusFilterChange"
  >
    <filter-sort-selector :data-source="dataSource" hide-archived />
    <q-dialog :model-value="editItem !== null" maximized persistent>
      <invoice-edit
        v-if="editItem !== null"
        :model-value="editItem"
        @update="onInvoiceCreated"
        @cancel="editItem = null"
      />
    </q-dialog>
    <q-dialog :model-value="detailItem !== null" maximized>
      <invoice-detail
        v-if="detailItem !== null"
        :model-value="detailItem"
        @update="onDetailChanged"
        @cancel="onDetailChanged"
      />
    </q-dialog>
    <q-card flat class="col q-pa-none" style="min-height: 0;">
      <q-card-section ref="listContainer" class="full-height q-pa-none">
        <dx-list
          v-show="!isEmptyDataSource"
          :data-source="dataSource"
          height="100%"
          page-load-mode="scrollBottom"
          class="events-list"
          item-template="item"
          selection-mode="none"
          :active-state-enabled="false"
          @initialized="e => listInstance = e.component"
        >
          <template #item="{ data: invoice }">
            <invoice-list-item
              :item="invoice"
              :class="{ 'row-highlight': invoice.id === highlightedRowId }"
              @click="detailItem = invoice"
            />
          </template>
        </dx-list>
        <div
          v-show="isEmptyDataSource"
          class="empty-container full-height"
        >
          <img
            src="/img/no-documents.svg"
            :style="{ maxWidth: '80vw', objectFit: 'contain' }"
            class="q-mb-xl"
          >
          <p class="text-h5">
            {{ $t('common.noInvoice') }}
          </p>
          <p class="sub text-weight-light">
            {{ $t('common.noInvoiceHint') }}
          </p>
        </div>
      </q-card-section>
    </q-card>
  </bottom-tabs-view>
</template>

